/**
 * Kit Selection Modal
 * Allows users to select a kit to receive
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Inventory as KitIcon,
  Add as AddIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import {
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import { rawMaterialKitsAPI } from '../../services/api';

const KitSelectionModal = ({ open, onClose, onSelectKit, onCreateKit, onEditKit, organization }) => {
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedKitForMenu, setSelectedKitForMenu] = useState(null);

  useEffect(() => {
    if (open) {
      fetchKits();
    }
  }, [open, organization]);

  const fetchKits = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await rawMaterialKitsAPI.getKits(organization);
      if (response.success) {
        setKits(response.data || []);
      } else {
        setError('Failed to load kits');
      }
    } catch (err) {
      console.error('Failed to fetch kits:', err);
      setError('Failed to load kits');
    } finally {
      setLoading(false);
    }
  };

  const filteredKits = kits.filter(kit =>
    kit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (kit.description && kit.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const activeKits = filteredKits.filter(kit => kit.isActive);

  const handleMenuOpen = (event, kit) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedKitForMenu(kit);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedKitForMenu(null);
  };

  const handleEditKit = (kit) => {
    handleMenuClose();
    if (onEditKit) {
      onEditKit(kit);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: '#039582', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KitIcon />
          <Typography variant="h6" fontWeight={600}>Select Kit to Receive</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search kits..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#039582' }} />
          </Box>
        ) : activeKits.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <KitIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {searchTerm ? 'No kits found matching your search' : 'No active kits available'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create a new kit template to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateKit}
              sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
            >
              Create New Kit
            </Button>
          </Box>
        ) : (
          <List>
            {activeKits.map((kit) => (
              <ListItem key={kit.id} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => onSelectKit(kit)}
                  sx={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 1,
                    pr: 1,
                    '&:hover': {
                      bgcolor: '#f0fdfa',
                      borderColor: '#039582',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {kit.name}
                        </Typography>
                        <Chip
                          label={`${kit.itemCount} items`}
                          size="small"
                          sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {kit.description || 'No description'}
                      </Typography>
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, kit)}
                    sx={{ ml: 1 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              </ListItem>
            ))}
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => handleEditKit(selectedKitForMenu)}>
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit Kit</ListItemText>
              </MenuItem>
            </Menu>
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCreateKit} startIcon={<AddIcon />} sx={{ color: '#039582' }}>
          Create New Kit
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KitSelectionModal;
